/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
/* eslint-disable check-react-ssr-usage/guard-browser-globals -- Pre-existing browser-global usage; this component renders client-side. */
import type { FC } from 'react';
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { UFOThirdPartySegment } from '@atlaskit/react-ufo/segment';

import { ConnectEosBanner } from '@atlassian/connect-module-core/connect-eos-banner';
import { isSystemApp } from '@atlassian/connect-module-core/is-system-app';
import { DataSourceProvider as ReferentialityDataSourceProvider } from '@atlassian/editor-referentiality/data-source-provider';

import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useExternalLinkHandlers } from '@confluence/adf-renderer/entry-points/useExternalLinkHandlers.ts';
import { ensureExternalLinksOpenInNewTab } from '@confluence/linking-platform/entry-points/ensureExternalLinksOpenInNewTab.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsAdmin } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { requireLegacyWRM } from '@confluence/wrm/entry-points/requireLegacyWRMHelpers.ts';
import { evalLegacyConnectInlineScripts } from '@confluence/connect-utils';
import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { FabricLegacyMacrosServerRenderer } from '@confluence/content-renderer-legacy-macros/entry-points/FabricLegacyMacrosServerRenderer.ts';
import { useSSRedLegacyMacroElement } from '@confluence/content-renderer-legacy-macros/entry-points/SSRedLegacyMacro.ts';
import { useConnectExtensionViewportSize } from '@confluence/content-renderer-legacy-macros/entry-points/useConnectExtensionViewportSize.ts';
import { LegacyMacroStyledElement } from '@confluence/content-renderer-legacy-macros/entry-points/LegacyMacroStyledElement.ts';
import { registerEmbeddedFausAppKeyListener } from '@confluence/forge-ui/entry-points/useEmbeddedMacroListeners.ts';
import {
	scopeCSS,
	mainContentSelector,
} from '@confluence/content-renderer-legacy-macros/entry-points/scopeStyleMacro.ts';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { ExtensionLoadingComplete } from '@confluence/extensions-performance/entry-points/ExtensionsPerformanceMetrics.ts';
import { LegacyUserElementsHover } from '@confluence/profile/entry-points/LegacyUserElementsHover.ts';
import { getLogger } from '@confluence/logger';
import {
	EDITOR,
	RENDERER,
	PDF,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import {
	teamCalendarWRM,
	loadMacroWebResources,
	type MacroRenderedOutputForSSR,
} from '@confluence/fabric-extension-lib/entry-points/macro-loaders.ts';
import {
	type ExtensionNodeType,
	isBodiedExtension,
} from '@confluence/fabric-extension-lib/entry-points/isBodiedExtension.ts';
import {
	INLINE_EXTENSION_TYPE,
	MULTI_BODIED_EXTENSION_TYPE,
	REGULAR_EXTENSION_TYPE,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { withNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext.ts';
import {
	checkIsDPModuleDefined,
	loadDataProviderModule,
} from '@confluence/app-referentiality/entry-points/DataProviderModule.ts';
import { defineMacroModule } from '@confluence/editor-multi-bodied-extension/entry-points/MacroModule.ts';
import { MultiBodiedExtensionHandler } from '@confluence/editor-multi-bodied-extension/entry-points/MultiBodiedExtensionHandler.ts';
import { NestedLegacyTocHydrator } from '@confluence/table-of-contents/entry-points/NestedLegacyTocHydrator.ts';
import { createHeadingLink } from '@confluence/editor-heading-links/entry-points/createHeadingLink.ts';

import { useUnknownMacroPortalConditional } from './useUnknownMacroPortal.tsx';
import type { ComponentProps } from './macroRendererTypes.ts';
import { LegacyMacroReferenceListener } from './LegacyMacroReferenceListener.tsx';
import { LegacyMacroRendererError } from './LegacyMacroRendererError.tsx';
import { useShouldHideOverlay } from './useShouldHideOverlay.ts';
import { useLinkIntercept } from './useLinkIntercept.ts';
import { parseWrmKeysFromTagsJs } from './helper/parseWrmKeysFromTagsJs.ts';
import { useMacroSLOExperienceTracking } from './useMacroSLOExperienceTracking.ts';

// The rendered HTML currently outputs a div that contains a macro id attribute
// `data-macro-id="XYZ"`. We can parse that and get the ID
const idFromStorageFormat = /data-macro-id="([0-9a-fA-F-]+?)"/;
// this will be fallback pattern in case div does not contain the macro id attribute.
// Parse in from data variable in the script tag.Pattern looks like `\\\"macro.id\\\":\\\"XYZ\\\"`
const idFromADF = /macro\.id\\":\\"([0-9a-fA-F-]+?)\\"/;
const originFromOutput = /"origin"\s*:\s*"(https?:\/\/[^"]+)"/;
const logger = getLogger('LegacyMacroRenderer');

const Overlay = () => (
	<div
		data-testid="legacy-macro-overlay"
		style={{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			position: 'absolute',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			top: 0,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			bottom: 0,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			left: 0,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			right: 0,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			cursor: 'default',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			zIndex: 2,
		}}
	/>
);

/**
 * CEMS-919 and HOT-84603
 * ---------
 * Current implementation of the Team Calendars Plugin does not account for
 * a SPA transition. It only loads the calendars once and forgets about
 * re-initializing them once the page is unmounted. For this, we need to
 * invoke the assets when necessary and initialize the calendars. Also reference:
 * confluence-frontend-server/src/components/ContentBody/ContentBodyDefaultComponent.js
 * @private
 */
const initializeCalendarIfNeeded = async (
	extensionKey: string,
	isBodied: boolean,
	macroRenderedOutput?: string | null,
) => {
	const isCalendarMacro =
		extensionKey === 'calendar' ||
		(isBodied && macroRenderedOutput?.includes('data-macro-name="calendar"'));

	if (
		!isCalendarMacro ||
		expValEquals('cc-engagement-team-calendar-macro-afm-migration', 'isEnabled', true)
	) {
		return;
	}

	requireLegacyWRM(
		teamCalendarWRM(),
		() => {
			try {
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- runs only inside the client-only requireLegacyWRM success callback
				window.require('tc/init-resources').requireResources();
			} catch (err) {
				logger.error(err);
			}
		},
		() => {
			logger.error`Error loading team calendar WRM!`;
		},
	);
};

const getMacroId = (adf, macroRenderedOutput): string => {
	// The `localId` fallback is only used for included-content TOC heading links;
	// gate it so all other legacy-macro id resolution matches prior behaviour.
	const idFromNode = expValEquals('confluence_toc_included_content_exp', 'isEnabled', true)
		? adf?.attrs?.parameters?.macroMetadata?.macroId?.value || adf?.attrs?.localId
		: adf?.attrs?.parameters?.macroMetadata?.macroId?.value;
	if (idFromNode) return idFromNode;

	let matches = idFromStorageFormat.exec(macroRenderedOutput || '');
	if (matches?.[1]) return matches[1];
	matches = idFromADF.exec(macroRenderedOutput || '');
	if (matches?.[1]) return matches[1];

	logger.error`Cannot find macro id from ${macroRenderedOutput}`;
	return '';
};

export const getMacroOrigin = (macroRenderedOutput?: string | null): string => {
	const matches = originFromOutput.exec(macroRenderedOutput || '');
	return matches?.[1] || '';
};

const getConnectAddonKeyFromMacroOutput = (
	macroRenderedOutput?: string | null,
): string | undefined =>
	typeof macroRenderedOutput === 'string'
		? (macroRenderedOutput.match(/"addon_key"\s*:\s*"([^"]+)"/)?.[1] ??
			macroRenderedOutput.match(/addon_key\\":\\"([^"]+)\\"/)?.[1])
		: undefined;

const MACROS_TO_URL_SHIM = [
	'include',
	'contentbylabel',
	'children',
	'content-report-table',
	'index',
	'pagetree',
	'tasks-report-macro',
	'recently-updated-dashboard',
	'recently-updated',
	'toc',
];

const UFO3PSegmentWrapper = ({ children, extensionKey }) => {
	const isFirstPartyExtension = useMemo(() => {
		const trimmedExtensions = new Set(
			StatsigConfigurations.getString('confluence_1p_extensions', '')
				.split(',')
				.map((item) => item.trim()),
		);
		return trimmedExtensions.has(extensionKey);
	}, [extensionKey]);

	if (isFirstPartyExtension) {
		return children;
	}

	return <UFOThirdPartySegment name={`extension-${extensionKey}`}>{children}</UFOThirdPartySegment>;
};

export const LegacyMacroRendererComponentClass: FC<ComponentProps> = withNavigationPolicy(
	({
		adf,
		macroRepresentation,
		macroRenderedOutput,
		mode,
		node,
		contentId,
		attributes,
		experienceName,
		featureFlags,
		webresource,
		body,
		parameters,
		navigationPolicy,
		references,
		multiBodiedExtensionActions,
		macroRenderedOutputFromSSR,
		macroOutput,
		spaceKey,
		providers,
		mediaToken,
		noOverlay,
		isWithinEditor,
		isPreviewMode,
		appKey,
		isConnectMacro,
		macroBodyStorage,
		getHeadingLinkPath,
	}) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const isInline = adf.type === INLINE_EXTENSION_TYPE;
		const isMultiBodied = adf.type === MULTI_BODIED_EXTENSION_TYPE;
		const extensionKey = adf?.attrs?.extensionKey || '';
		const scriptContainer = useRef<HTMLDivElement>(null);

		if (!macroRenderedOutput) macroRenderedOutput = '';

		if (extensionKey && macroRenderedOutput) {
			// If a Tiny page with Style Macro gets converted to Fabric, need to Scope the CSS
			// When in edit mode, the <style> tag don't have data-macro-name="style" attribute, so need to pass a different regex
			if (extensionKey === 'style') {
				macroRenderedOutput = scopeCSS(
					macroRenderedOutput,
					mainContentSelector,
					/(<style[^>]*>)((.|\n|\r)*?)(<\/style>)/gm,
				).contentWithScopedCSS as string;
			} else {
				// Other extensionKeys could have nested Style Macro
				macroRenderedOutput = scopeCSS(macroRenderedOutput, mainContentSelector)
					.contentWithScopedCSS as string;
			}
		}

		const { viewportHeight } = useConnectExtensionViewportSize(node);

		const shouldHideOverlay = useShouldHideOverlay(
			extensionKey,
			isBodiedExtension(adf.type as ExtensionNodeType),
		);

		/**
		 * Callback to be triggered when web resources are resolved.
		 * This is to make sure certain macros e.g. Gadget Macro only to render output after web resources loaded.
		 * For more context of the issue: https://hello.atlassian.net/wiki/spaces/~462393321/pages/1007234825/Debugging+Legacy+Jira+gadgets+within+Connie
		 * https://jira.atlassian.com/browse/CONFCLOUD-70573
		 */
		const [delayedOutput, setDelayedOutput] = useState<string>(
			extensionKey === 'gadget' ? '' : macroRenderedOutput,
		);
		const [container, setContainer] = useState<Element | null>(null);
		const [isMacroResourcesLoaded, setIsMacroResourcesLoaded] = useState<boolean>(false);

		const unknownMacroPortals = useUnknownMacroPortalConditional({
			container,
			macroBodyStorage: macroBodyStorage ?? null,
		});

		const { eventHandlers: externalLinkEventHandlers, onSetLinkTarget } = useExternalLinkHandlers();

		// Patch links in the raw server-rendered HTML, which bypass the ADF renderer.
		useLayoutEffect(() => {
			if (
				!container ||
				!(container instanceof HTMLElement) ||
				macroRepresentation === 'atlas_doc_format' ||
				!expValEquals('confluence_legacy_macro_external_links_new_tab', 'isEnabled', true)
			) {
				return;
			}
			ensureExternalLinksOpenInNewTab(container);
		}, [container, delayedOutput, macroRepresentation]);

		// Variables for temporarily measuring rendered macro heights
		const isRendererExtension = mode === RENDERER && adf.type === REGULAR_EXTENSION_TYPE;
		const isMacroLoaded = container && isMacroResourcesLoaded && delayedOutput;

		useEffect(() => {
			if (container) {
				const loadMacroResources = async () => {
					const isDPModuleDefined = checkIsDPModuleDefined();

					if (!isDPModuleDefined) {
						const isViewPage = mode === RENDERER;
						await loadDataProviderModule({
							logger,
							isViewPage,
							createAnalyticsEvent,
						});
					}

					if (isMultiBodied) {
						await defineMacroModule({
							logger,
						});
					}

					await loadMacroWebResources(
						webresource,
						macroRenderedOutputFromSSR as MacroRenderedOutputForSSR[],
						macroOutput,
						() => {
							if (delayedOutput !== macroRenderedOutput) {
								setDelayedOutput(macroRenderedOutput as string);
							}
						},
						scriptContainer?.current,
					);
					setIsMacroResourcesLoaded(true);
				};
				void loadMacroResources();
				void evalLegacyConnectInlineScripts(
					container,
					fg('content_early_connect_iframe_initialization'),
				);
				void initializeCalendarIfNeeded(
					extensionKey,
					isBodiedExtension(adf.type as ExtensionNodeType),
					macroRenderedOutput,
				);
			}
		}, [
			webresource,
			extensionKey,
			macroRenderedOutput,
			delayedOutput,
			container,
			scriptContainer,
			macroRenderedOutputFromSSR,
			macroOutput,
			mode,
			createAnalyticsEvent,
			isMultiBodied,
			adf.type,
		]);

		useEffect(() => {
			/**
			 * Our goal here is to measure the height of the connect macro after its iframe content has finished rendering.
			 * The iframe load event would be insufficient and we can't be specific on when the content in the iframe is done
			 * rendering since it'd rely on the vendor using ACJS to communicate this. At best we approximate by waiting for
			 * resources to have been loaded and waiting a reasonable amount of time to check. We will also restrict this
			 * to view mode and avoid multi-bodied or inline macros.
			 */
			if (isMacroLoaded && isRendererExtension && fg('confluence_content_macro_measurements')) {
				const timer = setTimeout(() => {
					// Target the connect iframe to prevent measuring macros in other extension points
					const connectIframe = container.querySelector('iframe');
					if (connectIframe) {
						const { height } = connectIframe.getBoundingClientRect();

						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								source: 'legacyMacroRenderer',
								action: 'render',
								actionSubject: 'connectMacro',
								attributes: {
									extensionKey,
									height,
								},
							},
						}).fire();
					}
				}, 10000);

				return () => clearTimeout(timer);
			}
		}, [container, extensionKey, createAnalyticsEvent, isMacroLoaded, isRendererExtension]);

		// Add instrumentation for WRM keys from data-wrm-key in webresource.tags.js
		// If extension is dependent on WRM resources, store WRM keys and extension key in window.__WRM_KEYS__
		useEffect(() => {
			const wrmKeys = parseWrmKeysFromTagsJs(webresource?.tags?.js ?? null) || [];
			if (window && wrmKeys.length > 0) {
				if (!window.__WRM_KEYS__) {
					window.__WRM_KEYS__ = {};
				}
				const existingJsKeys = window.__WRM_KEYS__['js'] ?? [];
				const existingExtensionKeys = window.__WRM_KEYS__['extension'] ?? [];
				window.__WRM_KEYS__['js'] = [...new Set([...existingJsKeys, ...wrmKeys])];
				window.__WRM_KEYS__['extension'] = [...new Set([...existingExtensionKeys, extensionKey])];
			}
		}, [webresource, extensionKey]);

		useEffect(() => {
			if (appKey && window) {
				if (!window.__APP_KEYS__) {
					window.__APP_KEYS__ = [];
				}
				window.__APP_KEYS__ = [...new Set([...window.__APP_KEYS__, appKey])];
			}
		}, [appKey]);

		useLinkIntercept(extensionKey ?? '', container, mode === EDITOR || isWithinEditor);

		const macroId = getMacroId(adf, macroRenderedOutput);
		const macroOrigin = getMacroOrigin(macroRenderedOutput);

		useMacroSLOExperienceTracking({
			contentId,
			delayedOutput,
			extensionKey,
			isConnectMacro,
			isRendererMode: mode === RENDERER,
			macroId,
			macroOutput,
			macroRenderedOutputFromSSR,
		});

		const ssredElement = useSSRedLegacyMacroElement(macroId);
		const { cloudId } = useSessionData();
		const isSiteAdmin = useIsAdmin();
		const connectAppKey =
			typeof appKey === 'string' && appKey.length > 0 && appKey !== 'unknown'
				? appKey
				: getConnectAddonKeyFromMacroOutput(macroRenderedOutput);
		const connectEosBanner =
			connectAppKey && fg('customer-messaging-iframe-banner-enabled') ? (
				<ConnectEosBanner
					appKey={connectAppKey}
					cloudId={cloudId}
					moduleType="dynamicContentMacros"
					isSystemApp={isSystemApp(connectAppKey)}
					isSiteAdmin={isSiteAdmin}
				/>
			) : null;

		useEffect(() => {
			// `appKey` from the GraphQL response can be the sentinel string "unknown"
			// (or empty/undefined). Fall back to the Connect `addon_key` parsed from
			// the rendered macro payload — that is the identifier embedded FAUS uses
			// to build the `fetchAppKeyFromEmbeddedFAUS_<addon_key>` channel name.
			const isValidAppKey = typeof appKey === 'string' && appKey.length > 0 && appKey !== 'unknown';
			const addonKeyCandidate = getConnectAddonKeyFromMacroOutput(macroRenderedOutput);
			const bridgeIdentifier = isValidAppKey ? appKey : addonKeyCandidate;
			const hasBridgeIdentifier =
				typeof bridgeIdentifier === 'string' && bridgeIdentifier.length > 0;
			if (!fg('forge_nbm_set_frame_ancestors') || !hasBridgeIdentifier) {
				return undefined;
			}
			let cleanupListener: (() => void) | undefined;
			let observer: MutationObserver | undefined;

			const registerWithIframeWindow = (iframe: HTMLIFrameElement | null): boolean => {
				const targetWindow = iframe?.contentWindow;
				if (!targetWindow) {
					return false;
				}

				cleanupListener?.();
				cleanupListener = registerEmbeddedFausAppKeyListener(
					bridgeIdentifier,
					macroOrigin,
					macroId,
					targetWindow,
				);
				return Boolean(cleanupListener);
			};

			const attachIframeLoadListener = (iframe: HTMLIFrameElement) => {
				const onLoad = () => {
					registerWithIframeWindow(iframe);
				};
				iframe.addEventListener('load', onLoad);
				return () => iframe.removeEventListener('load', onLoad);
			};

			let cleanupIframeLoadListener: (() => void) | undefined;

			const existingIframe =
				container instanceof HTMLElement ? container.querySelector('iframe') : null;
			if (existingIframe) {
				registerWithIframeWindow(existingIframe);
				cleanupIframeLoadListener = attachIframeLoadListener(existingIframe);
			} else if (container instanceof HTMLElement) {
				observer = new MutationObserver(() => {
					const iframe = container.querySelector('iframe');
					if (registerWithIframeWindow(iframe)) {
						observer?.disconnect();
						observer = undefined;
						if (iframe) {
							cleanupIframeLoadListener?.();
							cleanupIframeLoadListener = attachIframeLoadListener(iframe);
						}
					}
				});
				observer.observe(container, { childList: true, subtree: true });
			}

			return () => {
				observer?.disconnect();
				cleanupIframeLoadListener?.();
				cleanupListener?.();
			};
		}, [container, macroId, appKey, macroOrigin, macroRenderedOutput]);

		const nodeProps = {
			ref: setContainer,
		};
		const macroDataProps = {
			'data-fabric-macro': macroId,
			'data-macro-body': body || '',
			'data-macro-parameters': parameters || '',
			'data-node-type': extensionKey === 'include' ? 'include' : undefined,
		};

		// Use parent id if the macro source is different than the current page
		const getContentId = (adf, contentId) => {
			const parentId = adf?.attrs?.parameters?.macroParams?._parentId?.value;
			return parentId || contentId;
		};

		const { getExtensionHandlers } = providers;

		const extensionHandlers = getExtensionHandlers
			? getExtensionHandlers({
					adf:
						macroRepresentation == 'atlas_doc_format'
							? macroRenderedOutputFromSSR || macroOutput || macroRenderedOutput
							: adf,
					contentId: getContentId(adf, contentId),
					spaceKey,
					macroRenderedOutput: macroRenderedOutputFromSSR || macroOutput || macroRenderedOutput,
					shouldIncludeMacroWithReferencesHandlers: true,
					referentialityDataSourceProvider: new ReferentialityDataSourceProvider(),
					mediaToken,
				})
			: {};

		// Migrated server "section" macros render as legacy HTML on view pages, so the any nested TOCs
		// are getting incorrect headings which is considered a migration blocker.
		// As a quick fix, we hydrate this case by replacing server rendered HTML with our frontend TOC.
		const shouldHydrateNestedLegacyToc = mode === RENDERER && extensionKey === 'section';

		const elementProps = {
			adf,
			isInline,
			contentId: getContentId(adf, contentId),
			mode,
			extensionKey,
			nodeProps,
			macroRepresentation,
			macroDataProps,
			macroRenderedOutput: delayedOutput,
			extensionHandlers,
			mediaToken,
			headingIdPrefix:
				extensionKey === 'include' &&
				expValEquals('confluence_toc_included_content_exp', 'isEnabled', true)
					? macroId
					: undefined,
			ssredElement,
			delayIframe: false,
			height: viewportHeight,
		};

		if (mode === RENDERER || mode === PDF) {
			try {
				const macroId = attributes?.macroId ? attributes.macroId : '';
				const type = attributes?.type ? attributes.type : 'none';
				// on view page it will send macro load event
				// it will help to alert in case of multiple rendering of macro
				// event name: macro renderer (client)
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'renderer',
						actionSubject: 'macro',
						source: 'legacyMacroRenderer',
						attributes: {
							mode,
							extensionKey,
							macroId,
							contentId,
							type,
						},
					},
				}).fire();
			} catch (err) {
				logger.error`An error occurred while firing the analytics event for legacyMacroRendererComponent: ${err}`;
			}

			const finalExtensionKey = attributes?.extensionKey || extensionKey;

			/**
			 * https://product-fabric.atlassian.net/browse/CCP-1798
			 * If macroRenderedOutputFromSSR or macroOuput are passed as a prop, they will be used for SPA rehydration (rather
			 * than the MacroContentQuery). macroRenderedOutputFromSSR / macroOutput already have JS/CSS coming from ContentUnifiedQuery,
			 * so here we use the base components of LegacyMacroRendererComponent, but instead
			 * render FabricLegacyMacrosServerRenderer, which sets JS/CSS internally.
			 * https://product-fabric.atlassian.net/browse/DLRN-3182
			 * If macroOutput exists, it contains the full html (as a string) required to render the macro.
			 */
			if (macroRenderedOutputFromSSR || macroOutput) {
				const macroDefaultProps: ExtensionHandlerProps = {
					node,
					references,
					contentId,
					spaceKey: spaceKey || '',
					mode: RENDERER,
					extensionKey,
					attributes,
					isConnectMacro,
				};

				return (
					<UFO3PSegmentWrapper extensionKey={finalExtensionKey}>
						<UFOLoadHold
							name="LegacyMacroRendererComponentResourcesSSR"
							hold={!isMacroResourcesLoaded}
						>
							<LegacyMacroReferenceListener references={references} macroContainer={container} />
							<ExtensionLoadingComplete
								extensionKey={finalExtensionKey}
								contentId={contentId}
								node={node}
							/>
							{connectEosBanner}
							<FabricLegacyMacrosServerRenderer
								renderedByProvider
								macroRenderedOutput={macroRenderedOutputFromSSR}
								macroDefaultProps={macroDefaultProps}
								containerRef={setContainer}
								macroOutput={macroOutput}
							/>
							{shouldHydrateNestedLegacyToc && (
								<NestedLegacyTocHydrator
									container={container}
									sectionNode={adf}
									contentId={contentId}
									spaceKey={spaceKey}
									references={references}
								/>
							)}
							<LegacyUserElementsHover node={container} />
							{
								// WS-3295 - DO NOT REMOVE THIS LOADER
								// We need to load the legacy editor here to allow users to create/edit/view comments with the attachments macro
								extensionKey === 'attachments' && !isPreviewMode && (
									<RendererExtensionContext.Consumer>
										{({ EditorLoaderLoader }) => {
											// isWithinEditor will be true if the attachments macro is within a macro
											// that itself is being rendered in the editor, e.g., excerpt-include.
											return (
												!isWithinEditor &&
												!isPreviewMode &&
												EditorLoaderLoader && <EditorLoaderLoader />
											);
										}}
									</RendererExtensionContext.Consumer>
								)
							}
							<MacroExperienceSuccess
								name={experienceName}
								contentId={contentId}
								extensionKey={extensionKey}
								mode={mode}
								attributes={attributes}
							/>
						</UFOLoadHold>
					</UFO3PSegmentWrapper>
				);
			}

			// TODO: Detect TeamCalendar macro and enusre hold timing is correct for it
			return (
				<UFO3PSegmentWrapper extensionKey={finalExtensionKey}>
					<UFOLoadHold
						name="LegacyMacroRendererComponentResourcesNonSSR"
						hold={!isMacroResourcesLoaded}
					>
						<LegacyMacroReferenceListener references={references} macroContainer={container} />
						{isMultiBodied && (
							<MultiBodiedExtensionHandler
								macroId={macroId}
								macroParams={parameters}
								editorActions={multiBodiedExtensionActions}
								readonly
							/>
						)}

						{/* we don't want to push elements down so display none*/}
						<div
							dangerouslySetInnerHTML={{
								__html: `${webresource?.tags?.css || ''}`,
							}}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ display: 'none' }}
						/>
						<ExtensionLoadingComplete
							extensionKey={finalExtensionKey}
							contentId={contentId}
							node={node}
						/>
						{connectEosBanner}
						<LegacyMacroStyledElement
							renderedByProvider
							{...elementProps}
							macroRenderedOutput={
								MACROS_TO_URL_SHIM.includes(extensionKey)
									? navigationPolicy.shimHtmlUrls(delayedOutput)
									: delayedOutput
							}
						/>
						{unknownMacroPortals}
						{shouldHydrateNestedLegacyToc && (
							<NestedLegacyTocHydrator
								container={container}
								sectionNode={adf}
								contentId={contentId}
								spaceKey={spaceKey}
								references={references}
							/>
						)}
						<LegacyUserElementsHover node={container} />
						{
							// WS-3295 - DO NOT REMOVE THIS LOADER
							// We need to load the legacy editor here to allow users to create/edit/view comments with the attachments macro
							extensionKey === 'attachments' && !isPreviewMode && (
								<RendererExtensionContext.Consumer>
									{({ EditorLoaderLoader }) => {
										// isWithinEditor will be true if the attachments macro is within a macro
										// that itself is being rendered in the editor, e.g., excerpt-include.
										return !isWithinEditor && EditorLoaderLoader && <EditorLoaderLoader />;
									}}
								</RendererExtensionContext.Consumer>
							)
						}
						<div
							key="js-container"
							ref={scriptContainer}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ display: 'none' }}
						/>
						<MacroExperienceSuccess
							name={experienceName}
							contentId={contentId}
							extensionKey={extensionKey}
							mode={mode}
							attributes={attributes}
						/>
					</UFOLoadHold>
				</UFO3PSegmentWrapper>
			);
		}

		let parsedDelayedOutput;
		let parseError;
		let component;

		try {
			if (macroRepresentation === 'atlas_doc_format') {
				parsedDelayedOutput = JSON.parse(delayedOutput);
				component = (
					<ADFRenderer
						document={parsedDelayedOutput}
						contentId={getContentId(adf, contentId)}
						featureFlags={featureFlags ?? {}}
						extensionHandlers={extensionHandlers}
						mediaToken={mediaToken}
						appearance="full-width"
						eventHandlers={externalLinkEventHandlers}
						onSetLinkTarget={onSetLinkTarget}
						allowHeadingAnchorLinks={
							extensionKey === 'include' &&
							expValEquals('confluence_toc_included_content_exp', 'isEnabled', true)
								? {
										allowNestedHeaderLinks: true,
										getHeadingLink: (headingId: string) =>
											createHeadingLink(getHeadingLinkPath, headingId) ??
											(typeof window !== 'undefined'
												? new URL(
														`#${encodeURIComponent(headingId)}`,
														window.location.href,
													).toString()
												: `#${encodeURIComponent(headingId)}`),
									}
								: undefined
						}
						headingIdPrefix={
							extensionKey === 'include' &&
							expValEquals('confluence_toc_included_content_exp', 'isEnabled', true)
								? macroId
								: undefined
						}
					/>
				);
			}
		} catch (e) {
			parseError = e;
			logger.error`An error occurred while parsing adf output: ${delayedOutput}`;
			component = (
				<LegacyMacroRendererError
					error={parseError}
					node={node}
					mode={mode}
					extensionKey={extensionKey}
					contentId={contentId}
				/>
			);
		}

		return (
			<UFO3PSegmentWrapper extensionKey={extensionKey}>
				<UFOLoadHold
					name="LegacyMacroRendererComponentResourcesFabric"
					hold={!isMacroResourcesLoaded}
				>
					<div
						dangerouslySetInnerHTML={{
							__html: `${webresource?.tags?.css || ''}`,
						}}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={{ display: 'none' }}
					/>
					{isMultiBodied && (
						<MultiBodiedExtensionHandler
							macroId={macroId}
							macroParams={parameters}
							editorActions={multiBodiedExtensionActions}
						/>
					)}

					<LegacyMacroStyledElement
						renderedByProvider
						{...elementProps}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						style={
							viewportHeight && fg('confluence_connect_macro_preset_height')
								? { minHeight: `${viewportHeight}px` }
								: null
						}
					>
						{noOverlay || shouldHideOverlay || isMultiBodied ? null : <Overlay />}
						{connectEosBanner}

						<LegacyMacroReferenceListener references={references} macroContainer={container} />
						{macroRepresentation === 'atlas_doc_format' ? (
							<div data-testid="macro-adf-renderer" ref={setContainer}>
								{component}
							</div>
						) : (
							<div ref={setContainer} dangerouslySetInnerHTML={{ __html: `${delayedOutput}` }} />
						)}

						<MacroExperienceSuccess
							name={experienceName}
							contentId={contentId}
							extensionKey={extensionKey}
							mode={mode}
							attributes={attributes}
						/>
					</LegacyMacroStyledElement>
					<div
						key="js-container"
						ref={scriptContainer}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={{ display: 'none' }}
					/>
				</UFOLoadHold>
			</UFO3PSegmentWrapper>
		);
	},
);

export const LegacyMacroRendererComponent = (props: any): React.JSX.Element => {
	const rendererExtensionProviders = useContext(RendererExtensionContext);
	const classicEditorExtensionProviders = useContext(ClassicEditorContext);
	const isWithinEditor = classicEditorExtensionProviders?.isWithinEditor ?? false;

	const providers = {
		...classicEditorExtensionProviders,
		...rendererExtensionProviders,
	};
	const propsWithProviders = {
		...props,
		providers,
		isWithinEditor,
		getHeadingLinkPath:
			props.getHeadingLinkPath ?? classicEditorExtensionProviders?.getHeadingLinkPath,
	};
	return <LegacyMacroRendererComponentClass {...propsWithProviders} />;
};
